<script setup>
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { picoSearch } from '@chatwoot/pico-search';
import { getInboxIconByType } from 'dashboard/helper/inbox';

import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import Checkbox from 'dashboard/components-next/checkbox/Checkbox.vue';
import Icon from 'dashboard/components-next/icon/Icon.vue';
import Input from 'dashboard/components-next/input/Input.vue';

const props = defineProps({
  inboxes: {
    type: Array,
    default: () => [],
  },
  isLoading: {
    type: Boolean,
    default: false,
  },
  initialSelectedIds: {
    type: Array,
    default: () => [],
  },
});

const emit = defineEmits(['confirm', 'skip']);

const { t } = useI18n();
const dialogRef = ref(null);
const searchValue = ref('');
const selectedIds = ref([]);

const filteredInboxes = computed(() => {
  if (!searchValue.value) return props.inboxes;
  return picoSearch(props.inboxes, searchValue.value.toLowerCase(), ['name']);
});

const allSelected = computed(
  () =>
    props.inboxes.length > 0 &&
    selectedIds.value.length === props.inboxes.length
);
const someSelected = computed(
  () => selectedIds.value.length > 0 && !allSelected.value
);

const isSelected = inboxId => selectedIds.value.includes(inboxId);

const inboxIcon = inbox =>
  getInboxIconByType(
    inbox.channelType,
    inbox.medium,
    'line',
    inbox.voiceEnabled
  );

const toggleInbox = inboxId => {
  selectedIds.value = isSelected(inboxId)
    ? selectedIds.value.filter(id => id !== inboxId)
    : [...selectedIds.value, inboxId];
};

const toggleAll = () => {
  selectedIds.value = allSelected.value
    ? []
    : props.inboxes.map(inbox => inbox.id);
};

const open = () => {
  searchValue.value = '';
  selectedIds.value = [...props.initialSelectedIds];
  dialogRef.value?.open();
};

const close = () => {
  dialogRef.value?.close();
};

const handleConfirm = () => {
  emit('confirm', selectedIds.value);
};

defineExpose({ open, close });
</script>

<template>
  <Dialog
    ref="dialogRef"
    type="edit"
    width="xl"
    overflow-y-auto
    :title="
      t(
        'ASSIGNMENT_POLICY.AGENT_ASSIGNMENT_POLICY.CREATE.INBOX_SELECTION.TITLE'
      )
    "
    :description="
      t(
        'ASSIGNMENT_POLICY.AGENT_ASSIGNMENT_POLICY.CREATE.INBOX_SELECTION.DESCRIPTION'
      )
    "
    :confirm-button-label="
      t(
        'ASSIGNMENT_POLICY.AGENT_ASSIGNMENT_POLICY.CREATE.INBOX_SELECTION.CONFIRM',
        { count: selectedIds.length }
      )
    "
    :cancel-button-label="
      t('ASSIGNMENT_POLICY.AGENT_ASSIGNMENT_POLICY.CREATE.INBOX_SELECTION.SKIP')
    "
    :disable-confirm-button="selectedIds.length === 0"
    :is-loading="isLoading"
    @confirm="handleConfirm"
    @close="$emit('skip')"
  >
    <div class="flex flex-col gap-3">
      <Input
        v-model="searchValue"
        :placeholder="
          t(
            'ASSIGNMENT_POLICY.AGENT_ASSIGNMENT_POLICY.CREATE.INBOX_SELECTION.SEARCH'
          )
        "
        custom-input-class="bg-transparent !outline-none w-full ltr:!pl-10 rtl:!pr-10 h-10"
      >
        <template #prefix>
          <Icon
            icon="i-lucide-search"
            class="absolute -translate-y-1/2 text-n-slate-11 size-4 top-1/2 ltr:left-3 rtl:right-3"
          />
        </template>
      </Input>

      <button
        v-if="inboxes.length"
        type="button"
        class="flex items-center gap-3 rounded-lg px-3 py-2 text-left hover:bg-n-alpha-2"
        @click="toggleAll"
      >
        <Checkbox
          :model-value="allSelected"
          :indeterminate="someSelected"
          class="pointer-events-none"
        />
        <span class="text-sm font-medium text-n-slate-12">
          {{
            t(
              'ASSIGNMENT_POLICY.AGENT_ASSIGNMENT_POLICY.CREATE.INBOX_SELECTION.SELECT_ALL'
            )
          }}
        </span>
      </button>

      <div
        v-if="filteredInboxes.length"
        class="flex max-h-80 flex-col overflow-y-auto rounded-xl border border-n-weak"
      >
        <button
          v-for="inbox in filteredInboxes"
          :key="inbox.id"
          type="button"
          class="flex items-center gap-3 border-b border-n-weak px-3 py-3 text-left last:border-b-0 hover:bg-n-alpha-2"
          @click="toggleInbox(inbox.id)"
        >
          <Checkbox
            :model-value="isSelected(inbox.id)"
            class="pointer-events-none shrink-0"
          />
          <Icon
            :icon="inboxIcon(inbox)"
            class="size-4 shrink-0 text-n-slate-11"
          />
          <div class="flex min-w-0 flex-col">
            <span class="truncate text-sm font-medium text-n-slate-12">
              {{ inbox.name }}
            </span>
            <span
              v-if="inbox.email || inbox.phoneNumber"
              class="truncate text-xs text-n-slate-11"
            >
              {{ inbox.email || inbox.phoneNumber }}
            </span>
          </div>
        </button>
      </div>

      <p v-else class="mb-0 py-6 text-center text-sm text-n-slate-11">
        {{
          t(
            'ASSIGNMENT_POLICY.AGENT_ASSIGNMENT_POLICY.CREATE.INBOX_SELECTION.EMPTY'
          )
        }}
      </p>

      <p class="mb-0 text-xs text-n-slate-10">
        {{
          t(
            'ASSIGNMENT_POLICY.AGENT_ASSIGNMENT_POLICY.CREATE.INBOX_SELECTION.TRANSFER_NOTICE'
          )
        }}
      </p>
    </div>
  </Dialog>
</template>
